import { useCallback } from 'react';
import { TableOptions } from '@/types/table';
import Paginator from '@/components/shared/data/Paginator/Paginator';
import styles from './TablePaginator.module.scss';
import { DEFAULT_PAGE_SIZE, defaultSelectBoxOptions } from '../utils/constants';
import If from '@/storybook/components/If/If';
import { Table, PaginationState } from '@tanstack/react-table';
import PageInfo from '@/components/shared/data/PageInfo/PageInfo';
import cx from 'classnames';
import useTableStore from '../store/useTableStore';
import SingleSelect from '@/storybook/components/FormCollection/SingleSelect/SingleSelect';

interface TablePaginatorProps<T> {
  options?: TableOptions<T>;
  table: Table<T>;
  pagination: PaginationState;
  totalCount: number;
  tableId: string;
  handleDropdownChange?: (selectedValue: number | null) => void;
  isLoading?: boolean;
}

const PaginatorWithoutPageInfo = <T,>({ options, table, pagination, totalCount, tableId }: TablePaginatorProps<T>) => (
  <div className={cx({ [styles.paginator]: !options?.scrollMode })} data-testid="pagination-without-pageinfo">
    {!options?.scrollMode && (
      <Paginator table={table} pagination={pagination} rowCount={totalCount} tableId={tableId} />
    )}
  </div>
);

const PaginatorWithPageInfo = <T,>({ options, table, tableId, pagination, totalCount }: TablePaginatorProps<T>) => {
  const setPagination = useTableStore(x => x.setPagination);
  const isSelectBoxVisible = !!options?.paginationSelectBox;
  const selectBoxOptions = options?.paginationSelectBox ?? defaultSelectBoxOptions;
  const pageSize = pagination.pageSize ?? DEFAULT_PAGE_SIZE;

  const handleDropdownChange = useCallback(
    (selectedValue: number) => {
      setPagination(tableId, { pageIndex: 0, pageSize: selectedValue });
    },
    [setPagination, tableId],
  );

  return (
    <div className={styles.customPaginatorContainer} data-testid="pagination-with-pageinfo">
      <div className={styles.paginationInfoContainer}>
        <PageInfo tableId={tableId} totalRecords={totalCount} />
      </div>
      <div className={styles.dropdownContainer}>
        <If condition={isSelectBoxVisible && !!totalCount}>
          <SingleSelect
            options={selectBoxOptions}
            value={pageSize}
            onChange={handleDropdownChange}
            dropdownPosition={options?.paginationOptions?.dropdownPosition ?? 'up'}
            clearable={false}
          />
        </If>
        <Paginator table={table} pagination={pagination} rowCount={totalCount} tableId={tableId} />
      </div>
    </div>
  );
};

const TablePaginator = <T,>(props: TablePaginatorProps<T>) => {
  if (props?.options?.hidePagination) return null;

  const displayPageInfoAtBottom = props?.options?.displayPageInfoAtBottom || false;

  return displayPageInfoAtBottom ? <PaginatorWithPageInfo {...props} /> : <PaginatorWithoutPageInfo {...props} />;
};

export default TablePaginator;
